// ── ADMIN: BILLING → COMPS ─────────────────────────────────────────────
// Every comped or reduced-rate enrollment for one season, for Lori and her
// accountant to read at year end. Read-only on purpose: comps and reduced
// rates are set only by scripts/comp_enrollment.php and
// scripts/rate_override.php, so this page offers no way to change one.
//
// Rows come from GET /charges/comps (all seasons at once -- a handful per
// year), fetched on every visit rather than kept in DB, since nothing else in
// the portal reads them. Full price / family pays cover the months the
// enrollment is billed for; see BillingRoutes::comps().
import { apiFetch } from '../core/api.js';
import { currentSeasonYear } from '../core/domain-utils.js';
import { VIEWS, VIEW_INIT, viewPerformer } from '../core/routing.js';

let compRows = null;
let compSeason = null;

VIEWS['a-comps'] = () => `
  <div class="page-head">
    <h2 class="serif">Comps</h2>
    <div class="sub">Performers who are comped or pay a reduced rate, and what their season would otherwise cost.</div>
  </div>
  <div id="compBody"><p class="empty">Loading…</p></div>`;

VIEW_INIT['a-comps'] = async () => {
  try {
    compRows = await apiFetch('/charges/comps');
  } catch (e) {
    const body = document.getElementById('compBody');
    if (body) body.innerHTML = `<p class="empty">Couldn't load comps: ${esc(e.message)}</p>`;
    return;
  }
  const seasons = [...new Set(compRows.map(r => r.seasonYear))];
  if (compSeason === null || !seasons.includes(compSeason)) {
    compSeason = seasons.includes(currentSeasonYear()) ? currentSeasonYear() : (seasons[0] ?? currentSeasonYear());
  }
  renderComps();
};

function renderComps() {
  const body = document.getElementById('compBody');
  if (!body) return;
  const seasons = [...new Set([currentSeasonYear(), ...compRows.map(r => r.seasonYear)])].sort((a, b) => b - a);
  const rows = compRows.filter(r => r.seasonYear === compSeason);

  const picker = `
    <div class="filters">
      <div class="field"><label for="compSeason">Season</label><select id="compSeason">
        ${seasons.map(y => `<option value="${y}"${y === compSeason ? ' selected' : ''}>${seasonLabel(y)}</option>`).join('')}
      </select></div>
    </div>`;

  const arrangement = r => r.comp
    ? '<span class="pill pill-paid">Full comp</span>'
    : `<span class="pill pill-gold">${fmt$(r.rateOverride)}/mo</span>`;
  const money = n => `<td style="text-align:right;white-space:nowrap">${fmt$(n)}</td>`;
  // Fixed widths so the columns line up from one program's table to the next.
  const table = inner => `<div class="tbl-wrap"><table style="table-layout:fixed;min-width:520px">
      <colgroup><col style="width:40%"><col style="width:24%"><col style="width:18%"><col style="width:18%"></colgroup>
      ${inner}
    </table></div>`;
  const sum = (list, key) => list.reduce((s, r) => s + r[key], 0);
  const totalRow = (label, list) => `<tr style="font-weight:600">
      <td colspan="2">${label}</td>${money(sum(list, 'fullPrice'))}${money(sum(list, 'familyPays'))}
    </tr>`;

  // One table per program, in name order; rows keep the endpoint's
  // first-name order within each.
  const programs = [...new Set(rows.map(r => r.program))].sort((a, b) => a.localeCompare(b));
  const tables = programs.map(name => {
    const list = rows.filter(r => r.program === name);
    return `<div class="card">
      <div class="card-hd">${esc(name)} (${list.length})</div>
      ${table(`
        <tr><th>Performer</th><th>Arrangement</th><th style="text-align:right">Full price</th><th style="text-align:right">Family pays</th></tr>
        ${list.map(r => `<tr>
          <td><button class="tbl-link" data-performer="${r.performerId}">${esc(r.performer)}</button>${r.dropped ? ' <span style="font-size:12px;color:var(--muted)">dropped</span>' : ''}</td>
          <td>${arrangement(r)}</td>${money(r.fullPrice)}${money(r.familyPays)}
        </tr>`).join('')}
        ${totalRow('Total', list)}`)}
    </div>`;
  }).join('');

  // A season total only adds anything once there is more than one program.
  const seasonTotal = programs.length > 1 ? `<div class="card">${table(`
      <tr><th colspan="2"></th><th style="text-align:right">Full price</th><th style="text-align:right">Family pays</th></tr>
      ${totalRow('Season total, all programs', rows)}`)}</div>` : '';

  body.innerHTML = picker + (rows.length
    ? tables + seasonTotal
    : `<div class="card"><p class="empty">No comped or reduced-rate performers in ${seasonLabel(compSeason)}.</p></div>`);

  document.getElementById('compSeason').addEventListener('change', e => {
    compSeason = Number(e.target.value);
    renderComps();
  });
  body.querySelectorAll('[data-performer]').forEach(btn => {
    btn.addEventListener('click', () => viewPerformer(btn.dataset.performer));
  });
}
